/**
 * `?v=`, `?cell=`, `?el=` deep links (F-123, 2026-09-04): open the board at a
 * shared VIEW, or framed on a shared ITEM (core/share-link.ts for the
 * grammar; BoardScreen's menu and the F-27 toolbar for where they are copied).
 *
 *  - a VIEW link sets the camera outright — the recipient sees exactly the
 *    board units and zoom the sender was looking at;
 *  - an ITEM link pans and zooms until the cell/element sits centred in the
 *    viewport (core/canvas.ts `fitCamera`, capped at a readable 2×), selects
 *    it, and flashes a ring around it so the eye lands on the right thing
 *    even in a dense grid.
 *
 * A link is a POINTER at something that is not there yet when the route
 * mounts: the doc has to sync, the grid has to publish a layout that holds
 * the row, an element has to arrive. So, like the `?thread=` link
 * (comments/useThreadDeepLink), the effect RETRIES on every relevant change
 * until the target resolves, then honours the link ONCE and strips the
 * params (replace, not push — a reload must not yank a viewport the user has
 * since moved, and Back must not walk through a jump that is not a page).
 * A target that never appears (a deleted row) leaves the board where it
 * opened; the params are dropped after the first layout so they cannot fire
 * on a later, unrelated structural change.
 */
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useStore } from "zustand";

import { fitCamera, type CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import {
  CELL_PARAM,
  ELEMENT_PARAM,
  parseCellParam,
  parseViewParam,
  VIEW_PARAM,
} from "../../core/share-link.js";
import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import { cellRectFromLayout } from "../comments/anchoring.js";
import { resolveElementRect } from "../elements/contained.js";
import type { GridLayoutSnapshot } from "./GridTable.js";

/** Screen px of air around a framed item. */
export const FRAME_PADDING_PX = 96;

export interface ShareDeepLinkTargets {
  /** The grid's latest layout, or null before the first pass. */
  gridLayout: GridLayoutSnapshot | null;
  /** Select a cell by ids; false when the ids are not in the layout yet. */
  selectCell: (rowId: string, colId: string) => boolean;
  /** Select an element (clears the grid's selection). */
  selectElement: (elementId: string) => void;
  /** Draw the attention ring around a board rect. */
  flash: (rect: BoardRect) => void;
}

export function useShareDeepLink(
  session: BoardSession,
  canvas: CanvasStore,
  targets: ShareDeepLinkTargets,
): void {
  const [params, setParams] = useSearchParams();
  const view = params.get(VIEW_PARAM);
  const cellParam = params.get(CELL_PARAM);
  const elementParam = params.get(ELEMENT_PARAM);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  const synced = useStore(session.store, (s) => s.synced);
  const viewport = useStore(canvas, (s) => s.viewport);
  const { gridLayout, selectCell, selectElement, flash } = targets;
  /** Each param honoured once, even though the effect re-runs on every sync tick. */
  const honoured = useRef<{ view: string | null; cell: string | null; el: string | null }>({
    view: null,
    cell: null,
    el: null,
  });

  const strip = (...names: string[]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const name of names) next.delete(name);
        return next;
      },
      { replace: true },
    );

  // VIEW: needs only the viewport to be measured (a 0×0 viewport would place
  // the camera against a size that is about to change).
  useEffect(() => {
    if (view === null || honoured.current.view === view) return;
    if (viewport.width === 0 || viewport.height === 0) return;
    honoured.current.view = view;
    const camera = parseViewParam(view);
    if (camera !== null) canvas.getState().setCamera(camera);
    strip(VIEW_PARAM);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setParams is stable
  }, [view, viewport, canvas]);

  // CELL: wait for a layout that holds the row and column.
  useEffect(() => {
    if (cellParam === null || honoured.current.cell === cellParam) return;
    if (viewport.width === 0 || viewport.height === 0) return;
    const target = parseCellParam(cellParam);
    if (target === null) {
      honoured.current.cell = cellParam;
      strip(CELL_PARAM);
      return;
    }
    if (gridLayout === null) return; // no layout yet — try again on the first pass
    const rect = cellRectFromLayout(gridLayout, target.rowId, target.colId);
    if (rect === null) {
      // The layout is real and the cell is not in it. Before sync that means
      // "not arrived yet"; after, it means "deleted" and there is nothing to
      // frame — drop the link so it cannot fire on a later structural change.
      if (synced) {
        honoured.current.cell = cellParam;
        strip(CELL_PARAM);
      }
      return;
    }
    honoured.current.cell = cellParam;
    const state = canvas.getState();
    state.setCamera(fitCamera(rect, state.viewport, state.camera, { padding: FRAME_PADDING_PX }));
    selectCell(target.rowId, target.colId);
    flash(rect);
    strip(CELL_PARAM);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setParams is stable
  }, [cellParam, gridLayout, synced, viewport, canvas, selectCell, flash]);

  // ELEMENT: wait for the element to arrive (elementsVersion ticks on sync).
  useEffect(() => {
    if (elementParam === null || honoured.current.el === elementParam) return;
    if (viewport.width === 0 || viewport.height === 0) return;
    const element = session.board.getElement(elementParam);
    if (element === undefined) {
      if (synced) {
        honoured.current.el = elementParam;
        strip(ELEMENT_PARAM);
      }
      return;
    }
    // A contained element's rect comes from its cell — which needs the layout.
    if (element.anchor.kind === "cell" && gridLayout === null) return;
    honoured.current.el = elementParam;
    const rect = resolveElementRect(element, gridLayout);
    const state = canvas.getState();
    state.setCamera(fitCamera(rect, state.viewport, state.camera, { padding: FRAME_PADDING_PX }));
    selectElement(element.id);
    flash(rect);
    strip(ELEMENT_PARAM);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setParams is stable
  }, [elementParam, elementsVersion, gridLayout, synced, viewport, canvas, session, selectElement, flash]);
}
